import React, { useState, useEffect, useRef, memo } from 'react';
import L from "leaflet";
import "leaflet.markercluster";
import markerIconPng from "leaflet/dist/images/marker-icon.png";

const createMapWidget = function (ref) {
    const map = L.map('map', { attributionControl:false, scrollWheelZoom: false }).setView([63.33, 14.06], 4);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
    return map;
};

// Markers that land on the same spot on screen are collected into one pin. Clicking it
// lists everyone there rather than zooming, so a click always answers "who is here?" —
// zooming still pulls the group apart on its own.
//
// The group belongs to the map and is created once with it. Holding it in component
// state instead would strand the previous group on the map whenever the component
// remounted, leaving two pins claiming the same logopeder.
const createClusterGroup = function (map) {
    const clusterGroup = L.markerClusterGroup({
        showCoverageOnHover: false,
        zoomToBoundsOnClick: false,
        spiderfyOnMaxZoom: false,
        maxClusterRadius: 45,
        // The map fits its bounds and then caps the zoom the moment markers are added.
        // Animating that leaves a second cluster icon in the DOM mid-flight, which is
        // both a flicker for the reader and a duplicate pin for anything inspecting the
        // page. There is nothing to animate on first paint anyway.
        animate: false,
        animateAddingMarkers: false,
        iconCreateFunction: cluster => L.divIcon({
            html: `<span>${cluster.getChildCount()}</span>`,
            className: 'map-cluster',
            iconSize: [40, 40]
        })
    });

    clusterGroup.on('clusterclick', event => {
        const entries = event.layer.getAllChildMarkers().map(m => m.entry);
        event.layer
            .bindPopup(buildLocationCard(entries), {maxWidth: 320, minWidth: 300})
            .openPopup();
    });

    clusterGroup.addTo(map);
    return clusterGroup;
};

// A place with no pin of its own is stored as 0,0 and falls back to its locality's point.
// Everything that reads a position has to agree on that. When only the marker did, a
// practice that never placed a pin showed on the map but disappeared the moment a visitor
// filtered by distance, because 0,0 is about 6800 km from anywhere in Sweden.
const instanceLatitude = (l) => l.latitude || (l.location && l.location.latitude);
const instanceLongitude = (l) => l.longitude || (l.location && l.location.longitude);

function arrayEquals(a, b) {
    return Array.isArray(a) &&
        Array.isArray(b) &&
        a.length === b.length &&
        a.every((val, index) => val === b[index]);
}

const buildClusterRow = ({page, instance}, showLocationName) => {
    const logo = page.logo
        ? `<img class="map-list__logo" src="${escapeHtml(page.logo)}">`
        : `<span class="map-list__logo map-list__logo--empty"><i class="fa-solid fa-comment-dots"></i></span>`;
    const detail = instance.address || (showLocationName ? instance.location.name : '');
    const detailRow = detail
        ? `<span class="map-list__detail"><i class="fa-solid fa-map-pin"></i> ${escapeHtml(detail)}</span>`
        : '';
    return `
        <li class="map-list__item">
            ${logo}
            <span class="map-list__body">
                <a class="map-list__title" href="${getPageUrl(page)}">${escapeHtml(page.title)}</a>
                ${detailRow}
            </span>
        </li>`;
};

// One popup for every pin, whether it stands for one logoped or several. A pin with a
// single logoped is just this list with one row.
const buildLocationCard = (entries) => {
    const names = [...new Set(entries
        .map(e => e.instance.location && e.instance.location.name)
        .filter(Boolean))];
    const sameLocation = names.length === 1;
    const sorted = [...entries].sort((a, b) => a.page.title.localeCompare(b.page.title, 'sv'));

    const heading = sameLocation ? escapeHtml(names[0]) : 'Flera platser';
    const count = entries.length === 1 ? '1 logoped' : `${entries.length} logopeder`;
    // With one logoped the button is simply the place, as it has always been; with
    // several it becomes the way to see the rest of them.
    const footerLabel = entries.length === 1
        ? escapeHtml(names[0])
        : `Visa alla i ${escapeHtml(names[0])}`;
    const footer = sameLocation
        ? `<div class="map-card__footer">
               <a class="btn btn-primary btn-sm btn-location" href="${getLocationUrl(names[0])}">
                   <i class="fa-solid fa-location-dot"></i> ${footerLabel}</a>
           </div>`
        : '';

    return `
        <div class="map-card map-card--list">
            <div class="map-card__header">
                <h2>${heading}</h2>
                <span class="map-card__count">${count}</span>
            </div>
            <ul class="map-list">${sorted.map(e => buildClusterRow(e, !sameLocation)).join('')}</ul>
            ${footer}
        </div>`;
};

const Map = memo(({map, clusterGroup, pages}) => {
    const [markers, setMarkers] = useState();
    var LeafIcon = L.Icon.extend({
        options: {
            shadowSize:   [50, 64],
            iconAnchor:   [15, 40],
            shadowAnchor: [4, 62],
            popupAnchor:  [-3, -76]
        }
    });

    var greenIcon = new LeafIcon({iconUrl: markerIconPng, popupAnchor: [-2, -42]});

    useEffect(() => {
        if (map && clusterGroup) {
            clusterGroup.clearLayers();

            const newLocations = [];
            pages.forEach(p => {
                p.locationInstances.forEach(l => {
                    const longitude = instanceLongitude(l);
                    const latitude = instanceLatitude(l);
                    // A page with no usable coordinates would otherwise put a marker at
                    // NaN and take the whole map down with it.
                    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                        return;
                    }
                    const marker = L.marker([latitude, longitude], {icon: greenIcon});
                    marker.entry = {page: p, instance: l};
                    clusterGroup.addLayer(marker.bindPopup(
                        buildLocationCard([{page: p, instance: l}]),
                        {maxWidth: 320, minWidth: 300}));
                    newLocations.push(marker);
                })
            })

            setMarkers(newLocations);
            if (newLocations.length > 0) {
                map.fitBounds(clusterGroup.getBounds(), {padding: [80, 80]});
                if (map.getZoom() > 10) {
                    map.setZoom(10);
                }
            }
        }
    }, [pages, clusterGroup]);

    return (
        <div className={"map-box " + (markers && markers.length === 0 && 'empty' || '')}>
            <div id="map"></div>
            <div className="no-results-box"><span className='no-results'>Inga platser hittades</span></div>
        </div>
    );
}, (prevProps, newxtProps) => {
    return prevProps.map === newxtProps.map
        && prevProps.clusterGroup === newxtProps.clusterGroup
        && arrayEquals(prevProps.pages, newxtProps.pages);
});

const LocationMap = ({pages}) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const [map, setMap] = useState();
    const [clusterGroup, setClusterGroup] = useState();

    useEffect(() => {
        if (mapRef.current === null) {
          const m = createMapWidget(containerRef.current);
          mapRef.current = m;
          setMap(m);
          setClusterGroup(createClusterGroup(m));
        }
    }, []);

    return <Map map={map} clusterGroup={clusterGroup} pages={pages} />;
};

const urlParam = function (name) {
    return encodeURI(name.replace(/ /g, '_').toLowerCase());
}

const getPageUrl = (page) => {
    return `/sidor/${urlParam(page.title)}/${page.id}`;
};

const getLocationUrl = (name) => {
    return `/platser/ort/${urlParam(name)}`;
};

function mapDistanceBetweenPoints(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // metres
    const φ1 = lat1 * Math.PI/180; // φ, λ in radians
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lon2-lon1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    const d = R * c;
    return d/1000;
  }

const PageList = ({ pageIds, params, showList = true }) => {
    const [availablePages, setAllPages] = useState([]);
    const [filteredPages, setFilteredPages] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch all pages on mount
    useEffect(() => {
        const fetchAllPages = async () => {
            try {
                const response = await fetch("/api/pages");
                const data = await response.json();
                setAllPages(data);
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch pages:', error);
                setLoading(false);
            }
        };

        const fetchSinglePage = async () => {
            try {
                const response = await fetch("/api/pages/" + pageIds[0]);
                const data = await response.json();
                setAllPages([data]);
                setLoading(false);
            } catch (error) {
                console.debug('Failed to fetch page:', error);
                setAllPages([])
                setLoading(false);
            }
        };

        if (pageIds && pageIds.length == 1) {
            fetchSinglePage();
        } else {
            fetchAllPages();
        }
    }, []);

    const getFilteredInstances = (instances, params) => {
        if (params.countyId) {
            return instances.filter(l => l.location && l.location.countyId === params.countyId)
        } else if (params.municipalityId) {
            return instances.filter(l => l.location && l.location.municipalityId === params.municipalityId)
        } else if (params.locationId) {
            return instances.filter(l => l.location && l.location.id === params.locationId)
        } else if (params.longitude && params.latitude && params.distance) {
            return instances.filter(l => mapDistanceBetweenPoints(
                instanceLatitude(l), instanceLongitude(l), params.latitude, params.longitude) < params.distance)
        } else {
            return instances;
        }
    }

    // Filter pages when pageIds changes
    useEffect(() => {
        if (!pageIds) {
            setFilteredPages(availablePages);
            return;
        }

        let filtered = availablePages.filter(page => pageIds.includes(page.id));

        if (params && (params.locationId || params.countyId || params.municipalityId || params.longitude || params.latitude)) {
            filtered = filtered.map(p => ({
                ...p, locationInstances: getFilteredInstances(p.locationInstances, params)
            }));
        }

        setFilteredPages(filtered);
    }, [pageIds, availablePages]);

    if (loading) {
        return <div>Laddar...</div>;
    }

    const resultsList = filteredPages && filteredPages.length > 0 ? (
        <table className="resultlist">
            <thead>
                <tr>
                <th>Logoped</th>
                <th>Plats</th>
                </tr>
            </thead>
            <tbody>
                {filteredPages.sort((a, b) => a.title.localeCompare(b.title)).map(r => 
                            <tr key={r.id}>
                                <td>
                                    <a className={r.published ? '' : 'unpublished'} href={getPageUrl(r)}>{r.title}</a>
                                </td>
                                <td className="table-locations">
                                    {r.locationInstances.length > 0 && r.locationInstances.map((li, index) => <div key={index}>
                                        <a href={ getLocationUrl(li.location.name) }>{li.location.name}</a>
                                        { index < (r.locationInstances.length - 1) && <span>{index >= (r.locationInstances.length - 2) && ' och' || ','} </span> }
                                    </div>)}
                                </td>
                            </tr>
                )}
            </tbody>
        </table>
    ) : <div className="no-results-box"><span className="no-results">Inga platser hittades</span></div>;

    return (
        <div className="page-list">
            <div className="row">
                <div className="col-12">
                    <LocationMap pages={filteredPages}/>
                </div>
                {showList && <div className="col-12">
                    {resultsList}
                </div>}
            </div>
        </div>
    );
};

export default PageList; 